import {createApi} from '@reduxjs/toolkit/query/react';

import {AxiosRequestHeaders} from 'axios';

import httpBaseQuery from 'shared/api/rtk-query/httpBaseQuery';
import {ApiUrls} from 'shared/config/model/urls';

import {buildQuery} from 'shared/api/query';
import {providesList} from 'shared/api/rtk-query/RTKQueryHelper';

import IPagination from 'shared/api/model/IPagination';
import IHydraResponse from 'shared/api/model/HydraResponse';
import {paginationAdapter} from 'shared/api/paginationHelper';
import ICollectionParams from 'shared/api/model/ICollectionParams';
import SimpleResponse from 'shared/api/model/SimpleResponse';

import IUserOutput, {IUserInput} from '../model/IUser';

export const userAPI = createApi({
    reducerPath: 'userAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['User'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        getUsers: build.query<IPagination<IUserOutput>, ICollectionParams>({
            query: ({page, sort, filters}) => ({
                path: buildQuery(ApiUrls.USERS(page, sort, filters)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesList(result?.items, 'User'),
            transformResponse(
                rawResult: IHydraResponse<IUserOutput>,
            ): Promise<IPagination<IUserOutput>> | IPagination<IUserOutput> {
                return paginationAdapter(rawResult);
            },
        }),
        getUser: build.query<IUserOutput, number>({
            query: (id) => ({
                path: ApiUrls.USER(id),
            }),
            providesTags: (result, error, id) => [{ type: 'User', id }],
        }),
        updateUser: build.mutation<IUserOutput, Pick<IUserOutput, 'id' | 'first_name' | 'last_name' | 'timezone'>>({
            query: ({id, ...data}) => ({
                path: ApiUrls.USER(id),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ id, ...patch }, { dispatch }) {
                dispatch(
                    userAPI.util.updateQueryData('getUser', id, (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),
        deleteUser: build.mutation<void, number>({
            query: (id) => ({
                path: ApiUrls.USER(id),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [
                { type: 'User', id },
                { type: 'User', id: 'LIST' },
            ],
        }),
        requestUserDeletion: build.mutation<void, void>({
            query: () => ({
                path: ApiUrls.USER_REQUEST_DELETION,
                method: 'POST',
                data: {},
            }),
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),
        confirmUserDeletion: build.mutation<void, string>({
            query: (token) => ({
                path: ApiUrls.USER_CONFIRM_DELETION(token),
                method: 'POST',
                data: {},
            }),
        }),
        changeUserTariff: build.mutation<IUserOutput, Pick<IUserInput, 'id' | 'tariff' | 'active_to'>>({
            query: ({id, ...data}) => ({
                path: ApiUrls.USER_TARIFF(id),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ id, ...patch }, { dispatch }) {
                dispatch(
                    userAPI.util.updateQueryData('getUser', id, (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),
        changeUserBlock: build.mutation<IUserOutput, Pick<IUserInput, 'id' | 'blocked'>>({
            query: ({id, ...data}) => ({
                path: ApiUrls.USER_BLOCKED(id),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ id, ...patch }, { dispatch }) {
                dispatch(
                    userAPI.util.updateQueryData('getUser', id, (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),
        confirmUserEmail: build.mutation<IUserOutput, Pick<IUserInput, 'id' | 'email_confirmed'>>({
            query: ({id, ...data}) => ({
                path: ApiUrls.USER_CONFIRM_EMAIL(id),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ id, ...patch }, { dispatch }) {
                dispatch(
                    userAPI.util.updateQueryData('getUser', id, (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),
        toggleActiveUserEmail: build.mutation<SimpleResponse, number>({
            query: (id) => ({
                path: ApiUrls.USER_UPDATE_EMAIL_STATUS(id),
                method: 'POST',
                data: {},
            }),
        }),
        sendEmailTestNotify: build.mutation<IUserOutput, number>({
            query: (id) => ({
                path: ApiUrls.USER_SEND_EMAIL_TEST_NOTIFY(id),
                method: 'PUT',
                data: {},
            }),
        }),
        subscribeEmail: build.mutation<void, {email: string}>({
            query: (data) => ({
                path: ApiUrls.USER_SUBSCRIBE,
                method: 'POST',
                data,
            }),
        }),
        fixEmailStatus: build.mutation<IUserOutput, Pick<IUserOutput, 'id'>>({
            query: ({ id }) => ({
                path: ApiUrls.USER_RESET_EMAIL_ERROR(id),
                method: 'PUT',
                data: {
                    email_active: true,
                    last_email_error: null,
                },
            }),
            async onQueryStarted({ id }, { dispatch }) {
                dispatch(
                    userAPI.util.updateQueryData('getUser', id, (draft) => {
                        draft.email_active = true;
                        draft.last_email_error = null;
                    }),
                );
            },
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),
        changeUserNewsletterConsent: build.mutation<IUserOutput, { id: number; agreeToNewsletter: boolean }>({
            query: ({ id, agreeToNewsletter  }) => ({
                path: ApiUrls.USER_CHANGE_NEWSLETTER_CONSENT(id),
                method: 'PUT',
                data: { agree_to_newsletter: agreeToNewsletter },
            }),
            async onQueryStarted({ id, agreeToNewsletter }, { dispatch }) {
                dispatch(
                    userAPI.util.updateQueryData('getUser', id, (draft) => {
                        draft.agree_to_newsletter = agreeToNewsletter;
                    }),
                );
            },
            invalidatesTags: [{ type: 'User', id: 'LIST' }],
        }),
        resetUserHighlightTariff: build.mutation<IUserOutput, number>({
            query: (id) => ({
                path: ApiUrls.USER(id),
                method: 'PUT',
                data: {
                    highlight_tariff: false,
                },
            }),
            async onQueryStarted(id, { dispatch }) {
                dispatch(
                    userAPI.util.updateQueryData('getUser', id, (draft) => {
                        draft.highlight_tariff = false;
                    }),
                );
            },
            invalidatesTags: (result, error, id) => [{ type: 'User', id }],
        }),
    }),
});